import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    ArrowLeft, ArrowClockwise, CheckCircle, ChartLineUp, Image as ImageIcon,
    UsersThree, ClipboardText, CurrencyCircleDollar, Eye, CursorClick, Percent,
    Flask, Rocket, PauseCircle, PlayCircle, FacebookLogo,
} from "@phosphor-icons/react";
import { StatusPill, fmtMoney, fmtNum, fmtDay, CTAS } from "./adsShared";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const absUrl = (u) => (!u ? "" : /^https?:\/\//.test(u) ? u : `${BACKEND}${u}`);

const TABS = [["overview", "Overview", ChartLineUp], ["creatives", "Ads / Creatives", ImageIcon],
["audience", "Audience / Targeting", UsersThree], ["leads", "Leads", ClipboardText]];

function Metric({ icon: Icon, label, value, testid }) {
    return (
        <div className="rounded-2xl border border-stone-200 bg-white p-4" data-testid={testid}>
            <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-stone2"><Icon size={13} weight="fill" /> {label}</div>
            <div className="mt-1.5 font-heading text-2xl font-bold text-ink">{value}</div>
        </div>
    );
}

export default function CampaignDetail({ campaign: initial, status, onBack, onChanged }) {
    const [c, setC] = useState(initial);
    const [tab, setTab] = useState("overview");
    const [insights, setInsights] = useState(null);   // {available, insights, reason}
    const [loadingIns, setLoadingIns] = useState(false);
    const [leads, setLeads] = useState([]);
    const [busy, setBusy] = useState(false);

    const reloadCampaign = async () => {
        try { const { data } = await api.get(`/ads/campaigns/${initial.id}`); setC(data); } catch { }
    };
    const loadInsights = async () => {
        setLoadingIns(true);
        try { const { data } = await api.get(`/ads/campaigns/${initial.id}/insights`); setInsights(data); }
        catch (e) { setInsights({ available: false, reason: "error", insights: {} }); }
        finally { setLoadingIns(false); }
    };
    const loadLeads = async () => {
        try { const { data } = await api.get(`/ads/campaigns/${initial.id}/leads`); setLeads(data.leads || []); } catch { }
    };
    useEffect(() => { loadInsights(); loadLeads(); /* eslint-disable-next-line */ }, [initial.id]);

    const published = !!(c.meta && c.meta.campaign_id);

    const act = async (action, label) => {
        setBusy(true);
        try { await api.post(`/ads/campaigns/${c.id}/${action}`); toast.success(label); await reloadCampaign(); onChanged?.(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
        finally { setBusy(false); }
    };
    const publish = () => act("publish", "Published to Meta (paused)");
    const sendTestLead = async () => {
        try {
            await api.post(`/ads/campaigns/${c.id}/test-lead`, { full_name: "Test Lead", email: `test.${Date.now()}@example.com`, phone: "+919999999999", push_to_meta: published });
            toast.success("Test lead created & synced to Salesforce");
            await loadLeads(); onChanged?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    };
    const resync = async (l) => {
        try { await api.post(`/ads/leads/${l.id}/resync`); toast.success("Synced to Salesforce"); await loadLeads(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Sync failed"); }
    };

    const ins = insights?.insights || {};
    const noData = insights && !insights.available;
    const ctaLabel = (CTAS.find(([v]) => v === c.cta) || [, "Sign Up"])[1];
    const genderLabel = !(c.genders?.length) ? "All genders" : c.genders.includes(1) && !c.genders.includes(2) ? "Men" : c.genders.includes(2) && !c.genders.includes(1) ? "Women" : "All genders";

    return (
        <div data-testid="ads-campaign-detail">
            {/* header */}
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <button onClick={onBack} data-testid="detail-back" className="inline-flex items-center gap-1 text-sm text-stone2 hover:text-ink"><ArrowLeft size={16} /> All campaigns</button>
                    <div className="mt-2 flex items-center gap-3">
                        <h1 className="font-heading text-2xl font-bold">{c.name}</h1>
                        <StatusPill status={c.status} />
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-xs text-stone2">
                        <FacebookLogo size={13} weight="fill" className="text-[#1877F2]" /> Meta · Lead Generation · {(c.countries || []).join(", ") || "—"}
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    {c.status === "draft" && <button data-testid="detail-publish" disabled={busy} onClick={publish} className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"><Rocket size={14} weight="fill" /> Publish</button>}
                    {published && c.status === "active" && <button data-testid="detail-pause" disabled={busy} onClick={() => act("pause", "Campaign paused")} className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 px-4 py-2 text-sm font-semibold text-amber-700 disabled:opacity-40"><PauseCircle size={15} weight="fill" /> Pause</button>}
                    {published && c.status === "paused" && <button data-testid="detail-resume" disabled={busy} onClick={() => act("resume", "Campaign resumed")} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 px-4 py-2 text-sm font-semibold text-emerald-700 disabled:opacity-40"><PlayCircle size={15} weight="fill" /> Resume</button>}
                    <button data-testid="detail-testlead" onClick={sendTestLead} className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 px-4 py-2 text-sm font-semibold text-stone-700"><Flask size={14} weight="fill" /> Send test lead</button>
                </div>
            </div>

            {/* tabs */}
            <div className="mt-6 flex gap-1 border-b border-stone-200">
                {TABS.map(([k, label, Icon]) => (
                    <button key={k} data-testid={`detail-tab-${k}`} onClick={() => setTab(k)}
                        className={`flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${tab === k ? "border-brand text-ink" : "border-transparent text-stone2 hover:text-ink"}`}>
                        <Icon size={15} weight="fill" /> {label}{k === "leads" ? ` (${leads.length})` : ""}
                    </button>
                ))}
            </div>

            <div className="mt-6">
                {/* OVERVIEW */}
                {tab === "overview" && (
                    <div data-testid="detail-overview">
                        <div className="mb-3 flex items-center justify-between">
                            <h3 className="font-heading text-sm font-bold uppercase tracking-wide text-stone2">Advertising performance</h3>
                            <button data-testid="detail-refresh-metrics" onClick={loadInsights} disabled={loadingIns} className="inline-flex items-center gap-1.5 rounded-lg border border-stone-300 px-3 py-1.5 text-xs font-semibold text-stone-600 disabled:opacity-40"><ArrowClockwise size={13} className={loadingIns ? "animate-spin" : ""} /> Refresh</button>
                        </div>
                        {noData ? (
                            <div className="rounded-2xl border border-dashed border-stone-300 p-12 text-center" data-testid="detail-nodata">
                                <ChartLineUp size={40} weight="duotone" className="mx-auto text-stone-300" />
                                <p className="mt-3 font-heading text-lg font-bold">No performance data yet</p>
                                <p className="mt-1 text-sm text-stone2">
                                    {insights?.reason === "not_published" ? "Publish this campaign to Meta to start collecting metrics." : "Metrics appear here once your campaign is live and delivering on Meta."}
                                </p>
                            </div>
                        ) : (
                            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="detail-metrics">
                                <Metric icon={CurrencyCircleDollar} label="Spend" value={fmtMoney(ins.spend)} testid="metric-spend" />
                                <Metric icon={Eye} label="Impressions" value={fmtNum(ins.impressions)} testid="metric-impressions" />
                                <Metric icon={CursorClick} label="Clicks" value={fmtNum(ins.clicks)} testid="metric-clicks" />
                                <Metric icon={Percent} label="CTR" value={ins.ctr != null ? `${Number(ins.ctr).toFixed(2)}%` : "—"} testid="metric-ctr" />
                                <Metric icon={UsersThree} label="Leads" value={fmtNum(leads.length || ins.leads)} testid="metric-leads" />
                                <Metric icon={CurrencyCircleDollar} label="Cost / Lead" value={ins.cpl ? fmtMoney(ins.cpl) : "—"} testid="metric-cpl" />
                            </div>
                        )}
                    </div>
                )}

                {/* CREATIVES */}
                {tab === "creatives" && (
                    <div className="max-w-md" data-testid="detail-creatives">
                        <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
                            <div className="flex items-center gap-2 px-3 py-2">
                                <div className="grid h-8 w-8 place-items-center rounded-full bg-[#1877F2] text-white"><FacebookLogo size={16} weight="fill" /></div>
                                <div><div className="text-xs font-bold">{status?.page_name || "Your Page"}</div><div className="text-[10px] text-stone2">Sponsored</div></div>
                            </div>
                            {c.primary_text && <p className="px-3 pb-2 text-sm">{c.primary_text}</p>}
                            <div className="aspect-[1.91/1] bg-stone-100">
                                {c.image_url ? <img src={absUrl(c.image_url)} alt="creative" className="h-full w-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                                    : <div className="flex h-full flex-col items-center justify-center text-stone-300"><ImageIcon size={40} weight="duotone" /><span className="mt-1 text-xs">No creative image</span></div>}
                            </div>
                            <div className="flex items-center justify-between gap-2 bg-stone-50 px-3 py-2.5">
                                <div className="min-w-0"><div className="truncate text-[10px] uppercase text-stone2">{(c.link || "inspiroconnect.com").replace(/^https?:\/\//, "")}</div>
                                    <div className="truncate text-sm font-bold">{c.headline || "—"}</div>
                                    {c.description && <div className="truncate text-[11px] text-stone2">{c.description}</div>}</div>
                                <span className="shrink-0 rounded-lg bg-stone-200 px-3 py-1.5 text-xs font-bold">{ctaLabel}</span>
                            </div>
                        </div>
                        {c.meta?.form_id && <p className="mt-3 text-[11px] text-stone2">Meta form ID: <span className="font-mono">{c.meta.form_id}</span></p>}
                        {c.meta?.campaign_id && <p className="text-[11px] text-stone2">Meta campaign ID: <span className="font-mono">{c.meta.campaign_id}</span></p>}
                    </div>
                )}

                {/* AUDIENCE */}
                {tab === "audience" && (
                    <div className="max-w-md space-y-3" data-testid="detail-audience">
                        {[["Locations", (c.countries || []).join(", ") || "—"],
                        ["Age range", `${c.age_min}–${c.age_max}`],
                        ["Gender", genderLabel],
                        ["Budget", c.budget_type === "daily" ? `${fmtMoney(c.daily_budget)}/day` : `${fmtMoney(c.lifetime_budget)} lifetime`],
                        ["Schedule", `${c.start_date ? fmtDay(c.start_date) : "On approval"} → ${c.end_date ? fmtDay(c.end_date) : "Ongoing"}`]].map(([k, v]) => (
                            <div key={k} className="flex items-center justify-between border-b border-stone-100 py-2">
                                <span className="text-xs font-semibold uppercase tracking-wide text-stone2">{k}</span>
                                <span className="text-sm font-medium text-ink">{v}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* LEADS */}
                {tab === "leads" && (
                    <div data-testid="detail-leads">
                        {leads.length === 0 ? (
                            <div className="rounded-2xl border border-dashed border-stone-300 p-12 text-center text-sm text-stone2" data-testid="detail-leads-empty">
                                No leads yet. Send a test lead to verify the Salesforce sync, or wait for live form submissions.
                            </div>
                        ) : (
                            <div className="overflow-hidden rounded-xl border border-stone-200">
                                <table className="w-full text-sm">
                                    <thead className="bg-stone-50 text-left text-[11px] uppercase tracking-wide text-stone2">
                                        <tr><th className="px-3 py-2">Name</th><th className="px-3 py-2">Contact</th><th className="px-3 py-2">Source</th><th className="px-3 py-2">Salesforce</th></tr>
                                    </thead>
                                    <tbody>
                                        {leads.map((l) => (
                                            <tr key={l.id} data-testid={`detail-lead-${l.id}`} className="border-t border-stone-100">
                                                <td className="px-3 py-2 font-medium">{l.fields?.full_name || l.fields?.name || "—"}</td>
                                                <td className="px-3 py-2 text-stone2">{l.fields?.email}<br />{l.fields?.phone}</td>
                                                <td className="px-3 py-2"><span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-bold uppercase">{l.source}</span></td>
                                                <td className="px-3 py-2">
                                                    {l.synced ? <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle size={15} weight="fill" /> Synced</span>
                                                        : <button data-testid={`detail-lead-resync-${l.id}`} onClick={() => resync(l)} className="inline-flex items-center gap-1 text-amber-600 hover:underline"><ArrowClockwise size={14} /> Retry</button>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
